/**
 * Back In Stock Manager - Moon Moon Agency
 * Alerte retour en stock : modale unique + inscription Klaviyo
 */

/*********************************************************************************************
 * 🔔 BACK IN STOCK MANAGER
 */
const BackInStockManager = {
	config: {
		selectors: {
			dialog: '[data-back-in-stock-dialog]',
			open: '[data-back-in-stock-open]',
			close: '[data-back-in-stock-close]',
			form: '[data-back-in-stock-form]',
			title: '[data-back-in-stock-title]',
			error: '[data-back-in-stock-error]',
			success: '[data-back-in-stock-success]',
			variantInput: '[data-selected-variant-id]',
		},
		classes: {
			hidden: 'mm-none',
			flex: 'mm-flex',
			scrollLock: 'mm-variants-modal-open',
		},
		api: {
			endpoint: 'https://a.klaviyo.com/client/back-in-stock-subscriptions/',
			revision: '2026-07-15',
			variantPrefix: '$shopify:::$default:::',
		},
	},

	init() {
		this.dialog = window.getElement(this.config.selectors.dialog);
		if (!this.dialog) return;

		this.form = this.dialog.querySelector(this.config.selectors.form);
		this.title = this.dialog.querySelector(this.config.selectors.title);
		this.error = this.dialog.querySelector(this.config.selectors.error);
		this.success = this.dialog.querySelector(this.config.selectors.success);
		this.submitBtn = this.form?.querySelector('[type="submit"]');
		this.variantId = null;

		this._bindEvents();
		window.log('info', '🔔 [BACK_IN_STOCK]', 'Initialized');
	},

	_bindEvents() {
		document.addEventListener('click', (e) => {
			const trigger = e.target.closest(this.config.selectors.open);
			if (trigger) {
				e.preventDefault();
				this.open(trigger);
				return;
			}

			if (e.target.closest(this.config.selectors.close)) this.dialog.close();
		});

		this.dialog.addEventListener('click', (e) => {
			if (e.target === this.dialog) this.dialog.close();
		});

		this.dialog.addEventListener('close', () => {
			if (this._ownsScrollLock) document.body.classList.remove(this.config.classes.scrollLock);
			window.emit('global:back-in-stock-closed');
		});

		this.form?.addEventListener('submit', (e) => this._handleSubmit(e));
	},

	open(trigger) {
		this.variantId = this._resolveVariantId(trigger);
		if (!this.variantId) {
			window.log('warn', '🔔 [BACK_IN_STOCK]', 'Variant ID introuvable sur le trigger');
			return;
		}

		if (this.title) this.title.textContent = trigger.dataset.productTitle || '';
		this._reset();

		this.dialog.showModal();
		this._ownsScrollLock = !document.body.classList.contains(this.config.classes.scrollLock);
		document.body.classList.add(this.config.classes.scrollLock);
		window.emit('global:back-in-stock-opened', { variantId: this.variantId });
	},

	_resolveVariantId(trigger) {
		const form = trigger.closest('form');
		const selected = form?.querySelector(this.config.selectors.variantInput);
		return selected?.value || trigger.dataset.variantId || null;
	},

	_reset() {
		this.form?.classList.remove(this.config.classes.hidden);
		this.success?.classList.add(this.config.classes.hidden);
		this.success?.classList.remove(this.config.classes.flex);
		this._hideError();
		if (this.submitBtn) this.submitBtn.disabled = false;
	},

	_showError() {
		if (!this.error) return;
		this.error.textContent = this.dialog.dataset.errorText || '';
		this.error.classList.remove(this.config.classes.hidden);
	},

	_hideError() {
		if (!this.error) return;
		this.error.textContent = '';
		this.error.classList.add(this.config.classes.hidden);
	},

	async _handleSubmit(e) {
		e.preventDefault();
		this._hideError();

		const companyId = this.dialog.dataset.companyId;
		if (!companyId) {
			window.log('error', '🔔 [BACK_IN_STOCK]', 'settings.klaviyo_public_api_key manquant');
			this._showError();
			return;
		}

		const email = this.form.querySelector('input[name="email"]')?.value;
		if (!email) return;

		if (this.submitBtn) this.submitBtn.disabled = true;

		try {
			const response = await fetch(`${this.config.api.endpoint}?company_id=${encodeURIComponent(companyId)}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/vnd.api+json',
					Accept: 'application/vnd.api+json',
					Revision: this.config.api.revision,
				},
				body: JSON.stringify({
					data: {
						type: 'back-in-stock-subscription',
						attributes: {
							channels: ['EMAIL'],
							profile: {
								data: {
									type: 'profile',
									attributes: { email },
								},
							},
						},
						relationships: {
							variant: {
								data: {
									type: 'catalog-variant',
									id: `${this.config.api.variantPrefix}${this.variantId}`,
								},
							},
						},
					},
				}),
			});

			if (!response.ok) throw new Error(`HTTP ${response.status}`);

			this.form.classList.add(this.config.classes.hidden);
			this.success?.classList.remove(this.config.classes.hidden);
			this.success?.classList.add(this.config.classes.flex);
			window.emit('global:back-in-stock-subscribed', { variantId: this.variantId });
			window.log('info', '🔔 [BACK_IN_STOCK]', 'Subscribed', { variantId: this.variantId });
		} catch (error) {
			this._showError();
			window.log('error', '🔔 [BACK_IN_STOCK]', 'Subscription failed', { error: error.message });
		} finally {
			if (this.submitBtn) this.submitBtn.disabled = false;
		}
	},
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => BackInStockManager.init());
} else {
	BackInStockManager.init();
}
